/**
 * M4aSurgeon — surgical sample-table (stsz) repair for MP4-family audio files.
 *
 * The class it fixes: an .m4a/.mp4/.mov whose moov EXISTS but whose stsz
 * (sample size table) contains garbage entries — FFmpeg fails with
 * "Invalid sample size N | error reading header" and refuses the whole file,
 * although the audio payload (mdat) is fully intact. Root case: Eugenie
 * (paying customer, 2026-07-26) — a Zoom lesson recording on an SD card,
 * one 4-byte stsz entry corrupted, whole 23 MB file unplayable everywhere
 * (WMP, browser, FFmpeg). Telemetry err: "Invalid sample size -1235568011".
 *
 * The insight that makes this EXACT science, not guesswork: stco/co64 gives
 * chunk byte offsets, stsc gives samples-per-chunk, so every chunk's byte
 * length is known exactly (next chunk offset − this offset, mdat end for the
 * last). A single corrupt entry inside a chunk is recoverable EXACTLY:
 *     repaired = chunkLen − sum(other entries in that chunk)
 * POC verified byte-identical recovery against a pristine original.
 * Multiple corrupt entries in ONE chunk are ambiguous → approximate equal
 * split of the remaining bytes (brief glitch beats a dead file) with an
 * explicit `approximate` flag so the UI stays honest.
 *
 * Out of scope (decline honestly, existing paths handle messaging):
 *   - moov missing entirely (needs reference reconstruction — repair-video's
 *     Mp4Untrunc territory; for audio-only raw-AAC it's an open research gap)
 *   - stco/stsc themselves corrupt (no ground truth to anchor on)
 *   - fragmented MP4 (moof) — different architecture
 *
 * API (mirrors WavSurgeon):
 *   await M4aSurgeon.isMp4Family(file)   → boolean (ftyp scan, first 256 B)
 *   await M4aSurgeon.examine(file)       → { canRepair, issues[], traks, … }
 *   await M4aSurgeon.repair(file, {diagnosis}) → { ok, blob, fixes[], approximate }
 */
(function () {
'use strict';

if (window.M4aSurgeon && !window.M4aSurgeon.__isStub) return;

var SIZE_CAP_AUDIO = 512 * 1024;   // no real AAC frame exceeds this
var SIZE_CAP_VIDEO = 8 * 1024 * 1024;

function rd32(b, p) { return ((b[p] << 24) | (b[p+1] << 16) | (b[p+2] << 8) | b[p+3]) >>> 0; }
function rd64(b, p) { return rd32(b, p) * 4294967296 + rd32(b, p + 4); }
function wr32(b, p, v) { b[p] = (v >>> 24) & 0xff; b[p+1] = (v >>> 16) & 0xff; b[p+2] = (v >>> 8) & 0xff; b[p+3] = v & 0xff; }
function fourcc(b, p) { return String.fromCharCode(b[p], b[p+1], b[p+2], b[p+3]); }

async function isMp4Family(file) {
    if (!file || file.size < 16) return false;
    try {
        var head = new Uint8Array(await file.slice(0, 256).arrayBuffer());
        for (var i = 4; i + 4 <= head.length; i++) {
            if (head[i] === 0x66 && head[i+1] === 0x74 && head[i+2] === 0x79 && head[i+3] === 0x70) return true; // 'ftyp'
        }
    } catch (_) {}
    return false;
}

// Walk top-level boxes of the file. moov may sit before OR after mdat; we
// need moov fully in memory (sample tables live there) but never the mdat
// payload — so read box headers via small slices, then pull moov whole.
async function topBoxes(file) {
    var out = [];
    var p = 0;
    var size = file.size;
    var guard = 0;
    while (p + 8 <= size && guard++ < 64) {
        var hdr = new Uint8Array(await file.slice(p, Math.min(p + 16, size)).arrayBuffer());
        if (hdr.length < 8) break;
        var boxSize = rd32(hdr, 0);
        var type = fourcc(hdr, 4);
        if (boxSize === 1 && hdr.length >= 16) boxSize = rd64(hdr, 8); // largesize
        if (boxSize < 8 || p + boxSize > size + 8) { out.push({ type: type, start: p, size: size - p, truncated: true }); break; }
        out.push({ type: type, start: p, size: boxSize });
        p += boxSize;
    }
    return out;
}

function childBoxes(buf, start, end) {
    var out = [];
    var p = start;
    while (p + 8 <= end) {
        var s = rd32(buf, p);
        if (s < 8 || p + s > end) break;
        out.push({ type: fourcc(buf, p + 4), start: p, size: s });
        p += s;
    }
    return out;
}
function findChild(buf, start, end, type) {
    var kids = childBoxes(buf, start, end);
    for (var i = 0; i < kids.length; i++) if (kids[i].type === type) return kids[i];
    return null;
}

// Parse one trak's sample tables out of the moov buffer. Offsets are
// relative to the moov buffer (not the file).
function parseTrak(moovBuf, trak) {
    var mdia = findChild(moovBuf, trak.start + 8, trak.start + trak.size, 'mdia');
    if (!mdia) return null;
    var hdlr = findChild(moovBuf, mdia.start + 8, mdia.start + mdia.size, 'hdlr');
    var handler = hdlr ? fourcc(moovBuf, hdlr.start + 16) : '';
    var minf = findChild(moovBuf, mdia.start + 8, mdia.start + mdia.size, 'minf');
    if (!minf) return null;
    var stbl = findChild(moovBuf, minf.start + 8, minf.start + minf.size, 'stbl');
    if (!stbl) return null;
    var se = stbl.start + stbl.size;
    var stsz = findChild(moovBuf, stbl.start + 8, se, 'stsz');
    var stsc = findChild(moovBuf, stbl.start + 8, se, 'stsc');
    var stco = findChild(moovBuf, stbl.start + 8, se, 'stco');
    var co64 = stco ? null : findChild(moovBuf, stbl.start + 8, se, 'co64');
    if (!stsz || !stsc || (!stco && !co64)) return null;

    // stsz: [ver+flags][sample_size][count][entries…]
    var fixedSize = rd32(moovBuf, stsz.start + 12);
    var count = rd32(moovBuf, stsz.start + 16);
    var entriesAt = stsz.start + 20;
    if (fixedSize !== 0) return { handler: handler, fixedSize: fixedSize, count: count, healthy: true };
    if (entriesAt + count * 4 > stsz.start + stsz.size) return null; // count itself corrupt — decline
    var sizes = new Array(count);
    for (var i = 0; i < count; i++) sizes[i] = rd32(moovBuf, entriesAt + 4 * i);

    // stco/co64: [ver+flags][count][entries…]
    var chunkCount, chunkOffs = [];
    if (stco) {
        chunkCount = rd32(moovBuf, stco.start + 12);
        if (stco.start + 16 + chunkCount * 4 > stco.start + stco.size) return null;
        for (var c = 0; c < chunkCount; c++) chunkOffs.push(rd32(moovBuf, stco.start + 16 + 4 * c));
    } else {
        chunkCount = rd32(moovBuf, co64.start + 12);
        if (co64.start + 16 + chunkCount * 8 > co64.start + co64.size) return null;
        for (var c2 = 0; c2 < chunkCount; c2++) chunkOffs.push(rd64(moovBuf, co64.start + 16 + 8 * c2));
    }

    // stsc: [ver+flags][count][(first_chunk, samples_per_chunk, desc_id)…]
    var scCount = rd32(moovBuf, stsc.start + 12);
    if (stsc.start + 16 + scCount * 12 > stsc.start + stsc.size) return null;
    var runs = [];
    for (var r = 0; r < scCount; r++) {
        runs.push({ first: rd32(moovBuf, stsc.start + 16 + 12 * r), per: rd32(moovBuf, stsc.start + 20 + 12 * r) });
    }
    // Expand samples-per-chunk for every chunk.
    var perChunk = [];
    for (var ri = 0; ri < runs.length; ri++) {
        var lastChunk = ri + 1 < runs.length ? runs[ri + 1].first - 1 : chunkCount;
        for (var cc = runs[ri].first; cc <= lastChunk; cc++) perChunk.push(runs[ri].per);
    }
    if (perChunk.length !== chunkCount) return null;

    return {
        handler: handler, fixedSize: 0, count: count, sizes: sizes,
        stszEntriesAt: entriesAt, chunkOffs: chunkOffs, perChunk: perChunk
    };
}

async function examine(file) {
    var out = { canRepair: false, issues: [], traks: 0, corrupt: [], approximateNeeded: false };
    try {
        if (!(await isMp4Family(file))) { out.issues.push('not_mp4_family'); return out; }
        var boxes = await topBoxes(file);
        var moovBox = null, mdatBox = null;
        for (var i = 0; i < boxes.length; i++) {
            if (boxes[i].type === 'moov' && !boxes[i].truncated) moovBox = boxes[i];
            if (boxes[i].type === 'mdat') mdatBox = boxes[i];
            if (boxes[i].type === 'moof') { out.issues.push('fragmented_mp4'); return out; }
        }
        if (!moovBox) { out.issues.push('moov_missing'); return out; }
        if (!mdatBox) { out.issues.push('mdat_missing'); return out; }
        if (moovBox.size > 64 * 1024 * 1024) { out.issues.push('moov_too_large'); return out; }

        var moovBuf = new Uint8Array(await file.slice(moovBox.start, moovBox.start + moovBox.size).arrayBuffer());
        out._moov = { start: moovBox.start, size: moovBox.size };
        out._mdatEnd = Math.min(mdatBox.start + mdatBox.size, file.size);

        var traks = childBoxes(moovBuf, 8, moovBuf.length).filter(function (b) { return b.type === 'trak'; });
        out.traks = traks.length;
        out.handlers = [];
        // All chunk offsets across all traks — the next chunk (any trak) bounds this one.
        var parsed = [];
        var allOffs = [];
        for (var t = 0; t < traks.length; t++) {
            var pt = parseTrak(moovBuf, traks[t]);
            if (!pt) { out.issues.push('sample_tables_unparseable'); return out; }
            parsed.push(pt);
            out.handlers.push(pt.handler || '');
            if (!pt.healthy) for (var c = 0; c < pt.chunkOffs.length; c++) allOffs.push(pt.chunkOffs[c]);
        }
        allOffs.sort(function (a, b) { return a - b; });

        for (var ti = 0; ti < parsed.length; ti++) {
            var trk = parsed[ti];
            if (trk.healthy) continue;
            var cap = trk.handler === 'vide' ? SIZE_CAP_VIDEO : SIZE_CAP_AUDIO;
            var si = 0;
            for (var ci = 0; ci < trk.chunkOffs.length; ci++) {
                var cnt = trk.perChunk[ci];
                var chunkStart = trk.chunkOffs[ci];
                // bound: next chunk offset (ANY trak) after this one, else mdat end
                var bound = out._mdatEnd;
                for (var oi = 0; oi < allOffs.length; oi++) { if (allOffs[oi] > chunkStart) { bound = Math.min(bound, allOffs[oi]); break; } }
                var chunkLen = bound - chunkStart;
                if (chunkLen <= 0) { out.issues.push('chunk_bounds_invalid'); return out; }
                var bad = [];
                var goodSum = 0;
                for (var s = 0; s < cnt; s++) {
                    var v = trk.sizes[si + s];
                    if (v === 0 || v > cap || v > chunkLen) bad.push(s);
                    else goodSum += v;
                }
                if (bad.length === 0 && goodSum !== chunkLen && Math.abs(goodSum - chunkLen) > 8) {
                    // sums off but no obviously-insane entry — could be interleave
                    // subtleties; don't touch what we can't prove.
                    out.issues.push('chunk_sum_mismatch');
                }
                if (bad.length > 0) {
                    var remain = chunkLen - goodSum;
                    if (remain <= 0) { out.issues.push('chunk_unrecoverable'); return out; }
                    out.corrupt.push({
                        trak: ti, chunk: ci, entryBase: si, bad: bad,
                        remain: remain, exact: bad.length === 1,
                        stszEntriesAt: trk.stszEntriesAt
                    });
                    if (bad.length > 1) out.approximateNeeded = true;
                }
                si += cnt;
            }
        }
        if (out.corrupt.length > 0) {
            out.issues.push('stsz_corrupt');
            out.canRepair = true;
        } else if (out.issues.length === 0) {
            out.issues.push('none_detected');
        }
        return out;
    } catch (e) {
        out.issues.push('examine_failed');
        return out;
    }
}

async function repair(file, opts) {
    opts = opts || {};
    var diag = opts.diagnosis || (await examine(file));
    if (!diag.canRepair || !diag._moov) return { ok: false, reason: (diag.issues && diag.issues[0]) || 'unrepairable' };
    var moovBuf = new Uint8Array(await file.slice(diag._moov.start, diag._moov.start + diag._moov.size).arrayBuffer());
    var fixes = [];
    for (var i = 0; i < diag.corrupt.length; i++) {
        var c = diag.corrupt[i];
        if (c.exact) {
            var idx = c.entryBase + c.bad[0];
            var at = c.stszEntriesAt + 4 * idx;
            var was = rd32(moovBuf, at);
            wr32(moovBuf, at, c.remain);
            fixes.push({ entry: idx, from: was, to: c.remain, exact: true });
        } else {
            // Ambiguous: split remaining bytes equally; last one takes the remainder.
            var per = Math.floor(c.remain / c.bad.length);
            var acc = 0;
            for (var b = 0; b < c.bad.length; b++) {
                var idx2 = c.entryBase + c.bad[b];
                var at2 = c.stszEntriesAt + 4 * idx2;
                var val = (b === c.bad.length - 1) ? (c.remain - acc) : per;
                acc += val;
                var was2 = rd32(moovBuf, at2);
                wr32(moovBuf, at2, val);
                fixes.push({ entry: idx2, from: was2, to: val, exact: false });
            }
        }
    }
    // Splice: [before moov][patched moov][after moov] — file length unchanged.
    var blob = new Blob([
        file.slice(0, diag._moov.start),
        moovBuf,
        file.slice(diag._moov.start + diag._moov.size)
    ], { type: file.type || 'audio/mp4' });
    return { ok: true, blob: blob, fixes: fixes, approximate: !!diag.approximateNeeded };
}

window.M4aSurgeon = {
    __isStub: false,
    version: '1.0.0',
    isMp4Family: isMp4Family,
    examine: examine,
    repair: repair
};

})();
